import { createRouter, createWebHistory } from "vue-router"
import i18n from "../i18n"
import admin from "./admin"
import partner from "./partner"
import { user, userForeigner } from "./user"
import { account, accountForeigner } from "./account"
import { isInactive } from "@/constants/userStatus"
import store from "../store/store"
import roles from "../constants/roles"
import { META_PIXEL_EVENT_NAMES } from "@/constants/metaPixel";
import { storeFbclidFromUrl } from "@/helpers/metaPixel";
import { applyLanguageTags, toDefaultPath, toForeignerPath } from "@/helpers/seo";
import { FOREIGNER, INDO, STORAGE_KEYS } from "@/constants/constants"

// configure router
const routes = [...admin, ...user, ...userForeigner, ...account, ...accountForeigner, ...partner]

// Disable browser auto restoration that can land you at the bottom
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

const router = createRouter({
  // mode: 'history',
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 1) Preserve position on back/forward
    if (savedPosition) return savedPosition
    // 2) Hash anchors (keep smooth here if you like)
    if (to.hash) {
      return { el: to.hash, top: 0, behavior: 'smooth' }
    }
    // 3) Default: always go to top-left (no smooth; first paint stays snappy)
    return { left: 0, top: 0 }
  }
})

const isRoutePath = (to, routePath) => {
  const foreignerRoutePath = `${FOREIGNER.ROUTE_PATH_PREFIX}${routePath}`
  return to.path === routePath || to.path === foreignerRoutePath
}

const isRouteName = (to, routeName) => {
  const foreignerRouteName = `${routeName}${FOREIGNER.ROUTE_NAME_SUFFIX}`
  return to.name === routeName || to.path === foreignerRouteName
}

// Unknown URLs fall through to the catch-all and render NotFoundPage with HTTP
// 200 (nginx serves the SPA shell for everything), so they are soft 404s and
// must never be presented to crawlers as real pages.
const NOT_FOUND_ROUTE_PATH = "/:pathMatch(.*)*"
const isNotFoundRoute = (route) =>
  !route.matched.length || route.matched.some(record => record.path === NOT_FOUND_ROUTE_PATH)

// Query strings that produce near-duplicate or campaign-tagged URLs. These are
// resolved per navigation rather than written onto to.meta, because route meta
// objects are shared across navigations — mutating them made the directive stick
// to the clean URL after one visit with a query.
const NOINDEX_QUERY_PARAMS = [
  "page", "sort", "filter",
  "utm_source", "utm_medium", "utm_campaign",
  "ref", "fbclid", "gclid"
]

/**
 * The robots directive for a navigation, or null to leave the page indexable.
 * @param {import("vue-router").RouteLocationNormalized} to
 * @returns {string|null}
 */
const resolveRobots = (to) => {
  if (to.meta.robots) {
    return to.meta.robots
  }
  if (isNotFoundRoute(to)) {
    return "noindex, nofollow"
  }
  return NOINDEX_QUERY_PARAMS.some(param => to.query[param]) ? "noindex, follow" : null
}

// Not every page has a foreigner (/en) twin: admin/partner routes are never
// prefixed, and account.js only prefixes paths under /account (so e.g.
// /registration-complete exists on the default variant only). Ask the router
// instead of guessing, so hreflang never points at the 404 catch-all.
const hasForeignerVariant = (path) => {
  try {
    return !isNotFoundRoute(router.resolve(toForeignerPath(toDefaultPath(path))))
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        `[router.hasForeignerVariant] Failed to resolve foreigner variant for "${path}":`,
        error,
        error && error.stack
      )
    }
    return false
  }
}

const isAdminRoute = (route) => (route.path && route.path.includes("admin")) || (route.name && route.name.includes("admin"))
const isPartnerRoute = (route) => (route.path && route.path.includes("partner")) || (route.name && route.name.includes("partner"))

const isForeignerRoute = (route) =>
  (route.path && route.path.startsWith(FOREIGNER.ROUTE_PATH_PREFIX))
  || (route.name && route.name.endsWith(FOREIGNER.ROUTE_NAME_SUFFIX))

const updateToForeignerRoute = (route) => {
  if (route.path && !route.path.startsWith(FOREIGNER.ROUTE_PATH_PREFIX)) {
    route.path = `${FOREIGNER.ROUTE_PATH_PREFIX}${route.path}`
  }
  if (route.name && !route.name.endsWith(FOREIGNER.ROUTE_NAME_SUFFIX)) {
    route.name = `${route.name}${FOREIGNER.ROUTE_NAME_SUFFIX}`
  }
  return route
}

const updateToNormalRoute = (route) => {
  if (route.path && route.path.startsWith(FOREIGNER.ROUTE_PATH_PREFIX)) {
    route.path = route.path.substring(FOREIGNER.ROUTE_PATH_PREFIX.length) || "/"
  }
  if (route.name && route.name.endsWith(FOREIGNER.ROUTE_NAME_SUFFIX)) {
    route.name = route.name.split(FOREIGNER.ROUTE_NAME_SUFFIX)[0] || "home"
  }
  return route
}

const nextByLocale = (to, from, next, routeParams) => {
  if (isAdminRoute(to) || isPartnerRoute(to)) {
    next(routeParams)
  } else {
    let useForeignerRoute = false
    let user = store.getters.userProfile
    if (user && user.phone) {
      useForeignerRoute = !user.phone.startsWith(INDO.CALLING_CODE)
    } else if (store.getters.switchToNewLocation) {
      let browserLanguage = window.navigator.language
      let isBrowserLanguageIndo = (!browserLanguage || browserLanguage.toLowerCase() === "id" || browserLanguage.toLowerCase().startsWith("id-"))
      let geoLocation = store.getters.geoLocation
      if (!isBrowserLanguageIndo
        || (geoLocation.country_code !== INDO.COUNTRY_CODE && geoLocation.iso_country_code !== INDO.ISO_COUNTRY_CODE)) {
        useForeignerRoute = true
      }
    } else if (isForeignerRoute(to) || isForeignerRoute(from)) {
      useForeignerRoute = true
    }

    if (routeParams) {
      if (useForeignerRoute) {
        routeParams = updateToForeignerRoute(routeParams)
      } else {
        routeParams = updateToNormalRoute(routeParams)
      }
      return next(routeParams)
    } else {
      if (useForeignerRoute) {
        if (!isForeignerRoute(to)) {
          return next(updateToForeignerRoute({ path: to.path, name: to.name, query: to.query, params: to.params }))
        }
      } else {
        if (isForeignerRoute(to)) {
          return next(updateToNormalRoute({ path: to.path, name: to.name, query: to.query, params: to.params }))
        }
      }
    }
    next(routeParams)
  }
}

let fbclidStored = false
router.beforeEach((to, from, next) => {
  // Persist fbclid from the initial landing URL so auth/payment calls can use it
  // as a server-side fbc fallback when the _fbc cookie is unavailable.
  if (!fbclidStored) {
    fbclidStored = true
    storeFbclidFromUrl()
  }

  // redirect to login page if not logged in and trying to access a restricted page
  // window.document.title = to.meta && to.meta.title ? to.meta.title : 'Goro';
  to.meta.id = to.name
  const matched = (to.meta.authorize) || to.matched.find(r => r.meta.authorize)?.meta?.authorize
  const authorize = matched || null
  const { userProfile } = store.getters
  const storageAuth = localStorage.getItem(STORAGE_KEYS.AUTHORIZATION.key)

  if (isRoutePath(to, "/refer")) {
    if (storageAuth) {
      if (!isInactive()) {
        return nextByLocale(to, from, next, {path: "/"})
      } else {
        return nextByLocale(to, from, next, {name: "account", query: to.query})
      }
    } else {
      return nextByLocale(to, from, next, {name: "register", query: to.query})
    }
  }
  if (to.path.match(/\/referral-invite/)) {
    let inviteCode = null;
    const pathSegments = to.path.split('/');
    const inviteIndex = pathSegments.indexOf('referral-invite');
    if (inviteIndex !== -1 && pathSegments.length > inviteIndex + 1 && pathSegments[inviteIndex + 1]) {
      inviteCode = pathSegments[inviteIndex + 1];
    }
    // Clone the existing query parameters object
    const updatedQuery = {...to.query, code: inviteCode};
    if (storageAuth) {
      if (!isInactive()) {
        return nextByLocale(to, from, next, {path: "/"})
      } else {
        return nextByLocale(to, from, next, {name: "account", query: {...updatedQuery}})
      }
    } else {
      return nextByLocale(to, from, next, {name: "register", query: {...updatedQuery}})
    }
  }

  if (authorize) {
    if (!userProfile || !storageAuth) {
      // not logged in so redirect to login page with the return url
      if (to.name.startsWith("admin")) {
        return nextByLocale(to, from, next, { name: "loginAdmin", query: { redirect: to.path } })
      } else if (to.name.startsWith("partner")) {
        return nextByLocale(to, from, next, { name: "loginPartner", query: { redirect: to.path } })
      } else if (isRouteName(to, "pin") && to.query.token) {
        return nextByLocale(to, from, next, {
          name: "login",
          query: { redirect: `${to.path}?token=${to.query.token}` }
        })
      } else if (isRouteName(to, "confirmBankAccount") && to.query.code) {
        return nextByLocale(to, from, next, {
          name: "login",
          query: {redirect: `${to.path}?code=${to.query.code}`}
        })
      } else {
        return nextByLocale(to, from, next, {name: "login", query: {redirect: to.path}})
      }
    }
    // check if route is restricted by role
    if (authorize.length && !authorize.includes(userProfile.role)) {
      // role not authorised so redirect to home page
      if (userProfile.role === roles.Admin) {
        return nextByLocale(to, from, next, { name: "goroadmin" })
      } else if (userProfile.role === roles.Partner) {
        return nextByLocale(to, from, next, { name: "partner" })
      } else {
        return nextByLocale(to, from, next, { path: "/" })
      }
    }
  } else {
    if ((isRouteName(to, "login") || isRouteName(to, "register")
      || isRouteName(to, "forgotPassword") || isRouteName(to, "resetPassword"))
      && store.getters.userProfile && storageAuth) {
      return nextByLocale(to, from, next, { name: "assetsOverview" })
    } else if (isRoutePath(to, "/reset-password")) {
      const queryParams = to.query
      if (!queryParams.email || !queryParams.token) {
        nextByLocale(to, from, next, { path: "/" }) // Redirect to home
      }
    }
  }

  nextByLocale(to, from, next)
})

router.afterEach((to, from) => {
  //check to update locale
  let locale = i18n.global.locale.value
  if (to.path.startsWith(FOREIGNER.ROUTE_PATH_PREFIX)) {
    locale = FOREIGNER.LOCALE
  } else {
    let user = store.getters.userProfile
    if (user && user.phone && user.phone.startsWith(INDO.CALLING_CODE) && user.preferred_locale) {
      locale = user.preferred_locale
    } else {
      const preferredLocale = localStorage.getItem(STORAGE_KEYS.PREFERRED_LOCALE.key)
      if (preferredLocale) {
        locale = preferredLocale
      } else {
        let browserLanguage = window.navigator.language
        if (i18n.global.availableLocales.includes(browserLanguage)) {
          locale = browserLanguage
        }
      }
    }
  }
  if (to.query.lang && i18n.global.availableLocales.includes(to.query.lang)) {
    locale = to.query.lang
  }
  if (i18n.global.locale.value !== locale) {
    i18n.global.locale.value = locale
  }
})

// Meta Pixel: Track PageView on every route change
router.afterEach(() => {
  if (window.fbq) window.fbq('track', META_PIXEL_EVENT_NAMES.PAGE_VIEW)
})

// SEO: robots directive, <html lang>, canonical and hreflang for the resolved
// route. `failure` is set for cancelled/aborted navigations — routine here since
// every route component is a lazy import, and acting on one would describe the
// document with a URL the browser never landed on.
router.afterEach((to, from, failure) => {
  if (failure) {
    return
  }

  const robots = resolveRobots(to)

  const existingRobotsMeta = document.querySelector('meta[name="robots"]')
  if (existingRobotsMeta) {
    existingRobotsMeta.remove()
  }
  if (robots) {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = robots
    document.head.appendChild(meta)
  }

  applyLanguageTags({
    path: to.path,
    locale: i18n.global.locale.value,
    // A noindex page must not carry a canonical (the query is dropped, so it
    // would point at a different, indexable URL) or hreflang (Google discards
    // annotations aimed at noindex URLs and reports them as errors).
    isIndexable: !robots || !robots.includes("noindex"),
    hasForeignerVariant: hasForeignerVariant(to.path)
  })
})

// Lazy-loaded route chunks can 404 after a deploy replaces content-hashed
// filenames. Recover by doing a full page load of the target route (which
// fetches the new index.html and chunk manifest) instead of leaving the user
// stuck on the previous page. A sessionStorage guard prevents a reload loop
// when the failure is not deploy-related.
const CHUNK_RELOAD_GUARD = "goro-chunk-reload"
router.onError((error, to) => {
  const isChunkLoadError =
    error &&
    (error.name === "ChunkLoadError" ||
      /loading (css )?chunk .* failed|failed to fetch dynamically imported module/i.test(error.message || ""))
  if (!isChunkLoadError) {
    if (process.env.NODE_ENV !== "production") {
      console.error("[router.onError] Navigation error:", error)
    }
    return
  }
  if (sessionStorage.getItem(CHUNK_RELOAD_GUARD)) {
    if (process.env.NODE_ENV !== "production") {
      console.error("[router.onError] Chunk load failed again after reload:", error)
    }
    return
  }
  sessionStorage.setItem(CHUNK_RELOAD_GUARD, "1")
  window.location.assign(to && to.fullPath ? to.fullPath : window.location.href)
})

router.afterEach(() => {
  sessionStorage.removeItem(CHUNK_RELOAD_GUARD)
})

export default router
